import { useState, useEffect, createContext, useContext } from 'react'
import { supabase } from '../lib/supabase'
import type { User as SupabaseUser, Session } from '@supabase/supabase-js'

interface User {
  id: string
  email: string
  name?: string
  emailVerified: boolean
}

interface AuthState {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  session: Session | null
}

interface AuthContextType extends AuthState {
  signInWithEmail: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signUpWithEmail: (email: string, password: string, name?: string) => Promise<void>
  confirmSignUp: (email: string, confirmationCode: string) => Promise<void>
  signOut: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function useAuthState(): AuthContextType {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
    session: null
  })

  const mapSupabaseUser = (supabaseUser: SupabaseUser): User => ({
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    name: supabaseUser.user_metadata?.name || supabaseUser.user_metadata?.full_name,
    emailVerified: supabaseUser.email_confirmed_at != null
  })

  const refreshUser = async (): Promise<void> => {
    try {
      setState(prev => ({ ...prev, isLoading: true }))

      const { data: { session }, error } = await supabase.auth.getSession()

      if (error) {
        throw error
      }

      if (session?.user) {
        const user = mapSupabaseUser(session.user)
        setState({
          user,
          isLoading: false,
          isAuthenticated: true,
          session
        })
      } else {
        setState({
          user: null,
          isLoading: false,
          isAuthenticated: false,
          session: null
        })
      }
    } catch (error) {
      console.error('Error refreshing user:', error)
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        session: null
      })
    }
  }

  const signInWithEmail = async (email: string, password: string): Promise<void> => {
    try {
      setState(prev => ({ ...prev, isLoading: true }))

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) {
        throw new Error(error.message)
      }

      if (data.user) {
        const user = mapSupabaseUser(data.user)
        setState({
          user,
          isLoading: false,
          isAuthenticated: true,
          session: data.session
        })
      }
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }))
      throw error
    }
  }

  const signInWithGoogle = async (): Promise<void> => {
    try {
      setState(prev => ({ ...prev, isLoading: true }))

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })

      if (error) {
        throw new Error(error.message)
      }
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }))
      throw error
    }
  }

  const signUpWithEmail = async (email: string, password: string, name?: string): Promise<void> => {
    try {
      setState(prev => ({ ...prev, isLoading: true }))

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: name || email.split('@')[0]
          }
        }
      })

      if (error) {
        throw new Error(error.message)
      }

      setState(prev => ({ ...prev, isLoading: false }))
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }))
      throw error
    }
  }

  const confirmSignUp = async (email: string, confirmationCode: string): Promise<void> => {
    try {
      setState(prev => ({ ...prev, isLoading: true }))

      const { error } = await supabase.auth.verifyOtp({
        email,
        token: confirmationCode,
        type: 'signup'
      })

      if (error) {
        throw new Error(error.message)
      }

      setState(prev => ({ ...prev, isLoading: false }))
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }))
      throw error
    }
  }

  const signOut = async (): Promise<void> => {
    try {
      setState(prev => ({ ...prev, isLoading: true }))

      const { error } = await supabase.auth.signOut()

      if (error) {
        throw new Error(error.message)
      }

      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        session: null
      })
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }))
      throw error
    }
  }

  useEffect(() => {
    // Get initial session
    refreshUser()

    // Listen to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (session?.user) {
          const user = mapSupabaseUser(session.user)
          setState({
            user,
            isLoading: false,
            isAuthenticated: true,
            session
          })
        } else {
          setState({
            user: null,
            isLoading: false,
            isAuthenticated: false,
            session: null
          })
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  return {
    ...state,
    signInWithEmail,
    signInWithGoogle,
    signUpWithEmail,
    confirmSignUp,
    signOut,
    refreshUser
  }
}

export { AuthContext }